import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import classNames from "classnames";

import ModalContainer from "ui/ModalContainer";
import Popover from "ui/Popover";
import Select from "ui/Select";

import {
  useCurrentOrganization,
  useCurrentServer,
  useCurrentServerGroup,
} from "providers/WithCurrentOrganization";
import { useRouteTopLevelReference } from "utils/useRouteTopLevelReference";
import { useRoutes } from "utils/routes";

import {
  useNewWorkbookDatabaseDetailsQuery,
  useNewWorkbookDatabasesLazyQuery,
} from "./gql/Query.generated";

type ServerOption = {
  humanId: string;
  name: string;
};

type DatabaseOption = {
  id: string;
  datname: string;
  permittedToCreateWorkbooks: boolean;
};

const noPermissionTooltip = (
  <div>
    You are not allowed to create new workbooks: you do not have the{" "}
    <strong>Create Workbooks</strong> permission on this database.
  </div>
);

const NewWorkbookButton = () => {
  const { databaseId } = useRouteTopLevelReference();

  if (databaseId) {
    return <DatabaseNewWorkbookButton databaseId={databaseId} />;
  }
  return <NewWorkbookModalButton />;
};

const DatabaseNewWorkbookButton = ({ databaseId }: { databaseId: string }) => {
  const { databaseWorkbooksNew } = useRoutes();
  const { data, loading, error } = useNewWorkbookDatabaseDetailsQuery({
    variables: { databaseId },
  });

  if (loading || error || !data) {
    return null;
  }

  const permitted = data.getDatabaseDetails.permittedToCreateWorkbooks;
  return (
    <Popover content={permitted ? undefined : noPermissionTooltip}>
      <Link
        to={databaseWorkbooksNew(databaseId)}
        className={classNames(
          "btn btn-success",
          permitted ? "" : "pointer-events-none opacity-[.65]",
        )}
      >
        New Workbook
      </Link>
    </Popover>
  );
};

const NewWorkbookModalButton = () => {
  const { serverId, serverGroupId, organizationSlug } =
    useRouteTopLevelReference();
  const currentOrganization = useCurrentOrganization(true);
  const currentServer = useCurrentServer(true);
  const currentServerGroup = useCurrentServerGroup(true);
  const navigate = useNavigate();
  const { databaseWorkbooksNew } = useRoutes();

  const serverOptions: ServerOption[] = useMemo(() => {
    if (serverId && currentServer) {
      return [{ humanId: currentServer.humanId, name: currentServer.name }];
    }
    if (serverGroupId && currentServerGroup) {
      return currentServerGroup.servers.map((s) => ({
        humanId: s.humanId,
        name: s.name,
      }));
    }
    if (organizationSlug && currentOrganization) {
      const standalone = currentOrganization.servers.map((s) => ({
        humanId: s.humanId,
        name: s.name,
      }));
      const grouped = currentOrganization.serverGroups.flatMap((sg) =>
        sg.servers.map((s) => ({ humanId: s.humanId, name: s.name })),
      );
      const seen = new Set<string>();
      return [...standalone, ...grouped].filter((s) => {
        if (seen.has(s.humanId)) {
          return false;
        }
        seen.add(s.humanId);
        return true;
      });
    }
    return [];
  }, [
    serverId,
    serverGroupId,
    organizationSlug,
    currentServer,
    currentServerGroup,
    currentOrganization,
  ]);

  const [open, setOpen] = useState(false);
  const [selectedServer, setSelectedServer] = useState<ServerOption | null>(
    null,
  );
  const [selectedDatabase, setSelectedDatabase] =
    useState<DatabaseOption | null>(null);

  const [
    fetchDatabases,
    { data: dbData, loading: dbLoading, error: dbError, called: dbCalled },
  ] = useNewWorkbookDatabasesLazyQuery();

  useEffect(() => {
    if (open && selectedServer) {
      fetchDatabases({ variables: { serverId: selectedServer.humanId } });
    }
  }, [open, selectedServer, fetchDatabases]);

  const handleOpen = () => {
    const initialServer = serverId
      ? serverOptions.find((s) => s.humanId === serverId) ?? null
      : null;
    setSelectedServer(initialServer);
    setSelectedDatabase(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCreate = () => {
    if (!selectedDatabase) {
      return;
    }
    setOpen(false);
    navigate(databaseWorkbooksNew(selectedDatabase.id));
  };

  const databaseOptions: DatabaseOption[] =
    dbData?.getDatabases
      .filter((db) => !!db.datname)
      .map((db) => ({
        id: db.id,
        datname: db.datname as string,
        permittedToCreateWorkbooks: db.permittedToCreateWorkbooks,
      })) ?? [];

  const createDisabled =
    !selectedDatabase || !selectedDatabase.permittedToCreateWorkbooks;
  const createGatingContent =
    selectedDatabase && !selectedDatabase.permittedToCreateWorkbooks
      ? noPermissionTooltip
      : undefined;

  return (
    <>
      <button type="button" className="btn btn-success" onClick={handleOpen}>
        New Workbook
      </button>
      {open && (
        <ModalContainer
          title="New Workbook"
          className="[&>div:last-child]:overflow-visible !min-w-0"
          layout="centered"
          onClose={handleClose}
        >
          <div className="flex flex-col gap-4 lg:min-w-[560px]">
            <div className="form-group !mb-0">
              <label className="block mb-1 font-semibold">Server</label>
              <Select<ServerOption>
                placeholder="Select a server..."
                items={serverOptions}
                value={selectedServer}
                onChange={(s) => {
                  setSelectedServer(s);
                  setSelectedDatabase(null);
                }}
                itemToString={(item) => item?.name ?? ""}
                block
              />
            </div>
            <div className="form-group !mb-0 min-h-[58px]">
              <label className="block mb-1 font-semibold">Database</label>
              {!selectedServer ? (
                <div className="text-gray-500 italic">
                  Select a server first.
                </div>
              ) : dbLoading || !dbCalled ? (
                <div className="text-gray-500 italic">Loading databases…</div>
              ) : dbError ? (
                <div className="text-red-600">Failed to load databases.</div>
              ) : databaseOptions.length === 0 ? (
                <div className="text-gray-500 italic">
                  No databases available on this server.
                </div>
              ) : (
                <Select<DatabaseOption>
                  placeholder="Select a database..."
                  items={databaseOptions}
                  value={selectedDatabase}
                  onChange={setSelectedDatabase}
                  itemToString={(item) => item?.datname ?? ""}
                  block
                />
              )}
            </div>
            <div className="flex justify-end gap-2 mt-2">
              <button
                type="button"
                className="btn btn-default"
                onClick={handleClose}
              >
                Cancel
              </button>
              <Popover content={createGatingContent}>
                <button
                  type="button"
                  className={classNames(
                    "btn btn-success",
                    createDisabled ? "pointer-events-none opacity-[.65]" : "",
                  )}
                  onClick={handleCreate}
                  disabled={createDisabled}
                >
                  Create Workbook
                </button>
              </Popover>
            </div>
          </div>
        </ModalContainer>
      )}
    </>
  );
};

export default NewWorkbookButton;
